import { countryName, IPlace, subdivisionName } from '@vacationtracker/shared/data/countries'
import { PublicHolidaysRepository } from '@vacationtracker/shared/common/date-holidays-repository'
import { leaveLawPolicyForLeaveType } from '@vacationtracker/shared/functions/leave-laws'
import { LeaveLawsResponse } from '@vacationtracker/shared/types/leave-law'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

export const AUTODETECTED_NAME_PREFIX = '<Autodetected for Departments'

export const isAutodetectedName = (name: string): boolean => name.includes(AUTODETECTED_NAME_PREFIX)

/** The teams an autodetected location was grouped from, for the "please rename this" notice. */
export const autodetectedTeams = (name: string): string => name.replace(`${AUTODETECTED_NAME_PREFIX}: `, '').replace('>', '')

export interface IPlaceHolidays {
  /** The region when the place has one, otherwise the country - 'Bayern', not 'Germany · Bayern'. */
  place: string
  holidays: { name: string; date: string }[]
}

/**
 * The public holidays a place implies this year, or undefined when there are none.
 *
 * One function so the count offered on the form and the set actually imported can never disagree.
 * Filtered to 'public' because the sentence says "public holidays", and the unfiltered list
 * also carries bank and observance entries (Germany is 9 public but 30 in total).
 *
 * The bundled library covers 203 of our 250 countries. For the rest this returns undefined and the
 * caller shows nothing and imports nothing - not falling back to Google Calendar,
 * whose holidays are stored as CUSTOM and are then skipped by the yearly re-import.
 */
export const publicHolidaysForPlace = (place: IPlace, locale?: string): IPlaceHolidays | undefined => {
  if (!place.countryCode) return undefined

  const holidays = new PublicHolidaysRepository(locale).getHolidaysForCountry(new Date().getFullYear(), place.countryCode, place.subdivisionCode ?? undefined, 'public')

  if (!holidays?.length) return undefined

  return {
    place: subdivisionName(place) ?? countryName(place.countryCode) ?? place.countryCode,
    holidays: holidays.map((holiday) => ({ name: holiday.name, date: holiday.date.slice(0, 10) })),
  }
}

export interface IStatutoryPolicy {
  leaveTypeId: string
  leaveTypeName: string
  daysPerYear: number
  hasUnlimitedDays: boolean
}

/**
 * The leave policies a new location in this place will be given, mirroring what location-created
 * assigns: a policy exists only where the feed has an entitlement AND the company has a live leave
 * type that corresponds to it.
 *
 * Kept in step with the backend by sharing `leaveLawKeyForName` and `leaveLawPolicyFor`, so the
 * form cannot promise something the backend will not do.
 */
export interface IStatutoryPoliciesParams {
  place: IPlace
  /** The company's own leave types - a law with no matching type seeds nothing. */
  leaveTypes?: { id: string; name: string; isActive: boolean }[]
  laws?: LeaveLawsResponse
  plan?: SubscriptionPlan | null
}

export const statutoryPoliciesForPlace = ({ place, leaveTypes = [], laws, plan }: IStatutoryPoliciesParams): IStatutoryPolicy[] => {
  if (!place.countryCode || !laws) return []

  return leaveTypes
    .filter((leaveType) => leaveType.isActive)
    .flatMap((leaveType) => {
      const law = leaveLawPolicyForLeaveType({ laws, place, leaveTypeName: leaveType.name, plan })

      return law
        ? [
            {
              leaveTypeId: leaveType.id,
              leaveTypeName: leaveType.name,
              daysPerYear: law.daysPerYear,
              hasUnlimitedDays: law.hasUnlimitedDays,
            },
          ]
        : []
    })
}

// Holidays are stored per year and the field takes no year argument, so a location that imported
// 2025 but never 2026 has a row that must not count as configured.
// A multi-day holiday is stored as one row per day sharing a multiDayId; it counts once, as on the Holidays tab.
export const countHolidaysThisYear = (holidays?: { year: string | number; holidays: { multiDayId?: string | null }[] }[]): number => {
  const currentYear = new Date().getFullYear()
  const rows = holidays?.find((entry) => Number(entry.year) === currentYear)?.holidays ?? []
  const multiDayIds = new Set(rows.flatMap((row) => (row.multiDayId ? [row.multiDayId] : [])))
  return rows.filter((row) => !row.multiDayId).length + multiDayIds.size
}
